import { getACTInterpretationText } from '../renderers/ACTResultRenderer';
import { getAQLQInterpretationText } from '../renderers/AQLQResultRenderer';
import {
  getCATInterpretationText,
  getCATSeverityRangeLabel,
} from '../renderers/CATResultRenderer';
import { getHandgripInterpretationText } from '../renderers/HandgripResultRenderer';
import { getHADSInterpretationText } from '../renderers/HADSResultRenderer';
import { getLCADLInterpretationText } from '../renderers/LCADLResultRenderer';
import { getMLHFQInterpretationText } from '../renderers/MLHFQResultRenderer';
import { getMMRCInterpretationText } from '../renderers/MMRCResultRenderer';
import { getOneRMInterpretationText } from '../renderers/OneRMResultRenderer';
import { getPFSDQInterpretationText } from '../renderers/PFSDQMResultRenderer';
import { getPSQIInterpretationText } from '../renderers/PSQIResultRenderer';
import { getPemaxInterpretationText } from '../renderers/PemaxResultRenderer';
import { getPimaxInterpretationText } from '../renderers/PimaxResultRenderer';
import { getRespiratoryEnduranceInterpretationText } from '../renderers/RespiratoryEnduranceResultRenderer';
import { getSAQInterpretationText } from '../renderers/SAQResultRenderer';
import { getSGRQInterpretationText } from '../renderers/SGRQResultRenderer';
import { getSIndexInterpretationText } from '../renderers/SIndexResultRenderer';
import { calculateHADSScores } from '../tests/HADSTest';
import {
  buildClinicalReferenceDecision,
  getClinicalReferencePrimaryLabel,
  normalizeClinicalReferenceDecision,
} from '../references/clinicalReferenceRegistry';
import {
  getPeripheralStrengthInterpretationText,
} from '../peripheral-muscle/peripheralMuscleDomain';
import {
  summarizePemaxPayload,
  summarizePimaxPayload,
  summarizeSIndexPayload,
  type PemaxResultPayload,
  type PimaxResultPayload,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import {
  resolveESWTInterpretation,
  resolveISWTInterpretation,
} from '../shuttle-walk/shuttleWalkDomain';
import {
  resolveChesterInterpretation,
  resolveSixMSTInterpretation,
} from '../step-tests/stepTestsDomain';
import {
  STS_REFERENCE_CHAIR_HEIGHT_CM,
  getStsReferenceUnit,
  getStsReferenceValidationNote,
  summarizeStsResult,
  type StsTestKey,
} from '../sts/stsDomain';
import {
  resolveTC6Interpretation,
  summarizeTc6Result,
} from '../tc6/tc6Domain';
import {
  getTUGReferenceComparisonNote,
  summarizeTugResult,
} from '../tug/tugDomain';
import {
  summarizeGaitSpeedResult,
} from '../gait-speed/gaitSpeedDomain';
import { summarizePeakFlowPayload } from '../peak-flow/peakFlowDomain';
import { normalizePeakCoughFlowPayload, summarizePeakCoughFlowPayload } from '../peak-cough-flow/peakCoughFlowDomain';
import { summarizePosturalVitalCapacityPayload, summarizeSnipPayload } from '../respiratory-muscle/neuromuscularDomain';
import type { AssessmentResultV2 } from '../types';
import {
  getAssessmentInterpretationOverride,
  resolveAssessmentInterpretationText,
} from '../utils/interpretationOverride';
import type {
  AssessmentReportDomain,
  AssessmentReportFindingBasis,
  AssessmentReportFindingStatus,
} from './assessmentReportCatalogs';
import type {
  AssessmentReportMeasureSnapshotV2,
  AssessmentReportSourceSnapshotV2,
  AssessmentReportValiditySnapshotV2,
} from './assessmentReportDomain';
import {
  captureClinicalAssessmentSnapshot,
  extractAssessmentLongitudinalMeasures,
} from './assessmentLongitudinalDomain';

type UnknownRecord = Record<string, unknown>;

type AdapterSuggestion = {
  automaticInterpretation: string;
  status: AssessmentReportFindingStatus;
  basis: AssessmentReportFindingBasis;
  referenceLabel: string | null;
  validity: AssessmentReportValiditySnapshotV2;
  secondaryMeasures?: AssessmentReportMeasureSnapshotV2[];
  allowLegacyDirectInterpretation?: boolean;
};

export interface BuildAssessmentReportSourceSuggestionInput {
  result: AssessmentResultV2;
  domain: AssessmentReportDomain;
  testLabel: string;
  primaryMetric: string;
  originalClassification?: string | null;
  capturedAt: string;
}

const FALLBACK_VALIDITY_NOTE =
  'A validade não pôde ser determinada a partir dos dados estruturados disponíveis; o resultado é apresentado sem classificação clínica.';

const asRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' ? value as UnknownRecord : {};

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const readNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const isFiniteIntegerInRange = (
  value: unknown,
  minimum: number,
  maximum: number,
): value is number =>
  typeof value === 'number'
  && Number.isInteger(value)
  && value >= minimum
  && value <= maximum;

const formatNumber = (value: number, decimals = 0): string =>
  value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const calculateAgeAtAssessment = (birthDate: string, assessmentDate: string): number | null => {
  const birth = new Date(birthDate);
  const assessment = new Date(assessmentDate);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(assessment.getTime())) return null;

  let age = assessment.getFullYear() - birth.getFullYear();
  const monthDifference = assessment.getMonth() - birth.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && assessment.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

const hydrateRespiratoryPressurePayload = (
  result: AssessmentResultV2,
): PimaxResultPayload | PemaxResultPayload => {
  const payload = asRecord(result.payloadJson);
  const summary = asRecord(result.summaryJson);
  const storedContext = asRecord(payload.patientContext);
  const birthDate = readString(storedContext.birthDate, result.patientBirthDateSnapshot);
  const ageAtAssessment = birthDate ? calculateAgeAtAssessment(birthDate, result.assessmentDate) : null;

  return {
    ...payload,
    patientContext: {
      ...storedContext,
      birthDate: birthDate || null,
      ageYears: readNumber(storedContext.ageYears, summary.patientAgeYears, ageAtAssessment),
      sex: readString(storedContext.sex, summary.patientSex, result.patientSexSnapshot) || null,
      weightKg: readNumber(storedContext.weightKg, summary.patientWeightKg, result.patientWeightSnapshot),
      heightCm: readNumber(storedContext.heightCm, summary.patientHeightCm, result.patientHeightSnapshot),
      identifier: readString(storedContext.identifier, result.patientMedicalRecordSnapshot) || null,
    },
  } as PimaxResultPayload | PemaxResultPayload;
};

const hydrateSIndexPayload = (result: AssessmentResultV2): SIndexResultPayload => {
  const payload = asRecord(result.payloadJson);
  const patientContext = asRecord(hydrateRespiratoryPressurePayload(result).patientContext);

  return {
    ...payload,
    patientContext,
    patientAge: readNumber(payload.patientAge, patientContext.ageYears),
    patientSex: readString(payload.patientSex, patientContext.sex) || null,
    patientWeight: readNumber(payload.patientWeight, patientContext.weightKg),
    patientHeight: readNumber(payload.patientHeight, patientContext.heightCm),
  } as SIndexResultPayload;
};

const measure = (
  key: string,
  label: string,
  rawValue: string | number | boolean | null,
  formattedValue: string,
  unit: string | null = null,
): AssessmentReportMeasureSnapshotV2 => ({
  key,
  label,
  rawValue,
  formattedValue,
  unit,
});

const defaultSuggestion = (automaticInterpretation = ''): AdapterSuggestion => ({
  automaticInterpretation,
  status: 'not_classified',
  basis: 'descriptive_only',
  referenceLabel: null,
  validity: {
    status: 'not_assessed',
    note: FALLBACK_VALIDITY_NOTE,
    limitations: [],
  },
});

const invalidSuggestion = (
  automaticInterpretation: string,
  note: string,
  secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [],
): AdapterSuggestion => ({
  automaticInterpretation,
  status: 'not_interpretable',
  basis: 'descriptive_only',
  referenceLabel: null,
  validity: {
    status: 'invalid',
    note,
    limitations: [],
  },
  secondaryMeasures,
});

const buildCatSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const payload = asRecord(result.payloadJson);
  const summary = asRecord(result.summaryJson);
  const total = readNumber(payload.total, summary.total);
  const answers = Array.isArray(payload.answers) ? payload.answers : [];
  const answersComplete =
    answers.length === 8
    && answers.every((answer) => isFiniteIntegerInRange(answer, 0, 5));
  const computedTotal = answersComplete
    ? (answers as number[]).reduce((sum, answer) => sum + answer, 0)
    : null;
  const totalValid = isFiniteIntegerInRange(total, 0, 40);
  const automaticInterpretation = getCATInterpretationText(total);
  const secondaryMeasures = [
    measure(
      'answered_items',
      'Itens respondidos',
      answers.length,
      `${answers.length}/8`,
      'itens',
    ),
  ];

  if (!answersComplete) {
    return {
      ...defaultSuggestion(automaticInterpretation),
      validity: {
        status: 'not_assessed',
        note:
          'As oito respostas estruturadas necessárias para conferir a pontuação do CAT não estão disponíveis ou estão incompletas.',
        limitations: [],
      },
      secondaryMeasures,
    };
  }

  if (!totalValid || computedTotal !== total) {
    return invalidSuggestion(
      automaticInterpretation,
      'A pontuação total do CAT não é compatível com a soma das oito respostas registradas.',
      secondaryMeasures,
    );
  }

  const status: AssessmentReportFindingStatus =
    total <= 10
      ? 'within_expected'
      : total <= 20
        ? 'borderline'
        : 'altered';

  return {
    automaticInterpretation,
    status,
    basis: 'instrument_reference',
    referenceLabel: `CAT — ${getCATSeverityRangeLabel(total)}`,
    validity: {
      status: 'valid',
      note: null,
      limitations: [],
    },
    secondaryMeasures,
  };
};

const buildHadsSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const payload = asRecord(result.payloadJson);
  const summary = asRecord(result.summaryJson);
  const anxietyScore = readNumber(payload.anxietyScore, summary.anxietyScore);
  const depressionScore = readNumber(payload.depressionScore, summary.depressionScore);
  const answers = Array.isArray(payload.answers) ? payload.answers : [];
  const answersComplete = answers.length === 14
    && answers.every((answer) => isFiniteIntegerInRange(answer, 0, 3));
  const validatedScoring = result.testVersion >= 2
    && payload.instrumentVersion === 'HADS_BR_BOTEGA_1995';
  const recordedScores = [
    anxietyScore == null ? '' : `ansiedade ${formatNumber(anxietyScore, 0)}/21`,
    depressionScore == null ? '' : `depressão ${formatNumber(depressionScore, 0)}/21`,
  ].filter(Boolean);
  const automaticInterpretation = recordedScores.length > 0
    ? `Pontuações registradas no HADS: ${recordedScores.join(' e ')}. O instrumento é de rastreio e não substitui diagnóstico clínico.`
    : 'O HADS foi registrado, mas não há pontuações suficientes para apresentar o resultado neste relatório.';
  const secondaryMeasures = [
    measure(
      'anxiety_score',
      'Ansiedade',
      anxietyScore,
      anxietyScore == null
        ? 'Não disponível'
        : `${formatNumber(anxietyScore, 0)}/21`,
      'pontos',
    ),
    measure(
      'depression_score',
      'Depressão',
      depressionScore,
      depressionScore == null
        ? 'Não disponível'
        : `${formatNumber(depressionScore, 0)}/21`,
      'pontos',
    ),
  ];

  const hasInvalidRecordedScore =
    (anxietyScore != null && !isFiniteIntegerInRange(anxietyScore, 0, 21))
    || (depressionScore != null && !isFiniteIntegerInRange(depressionScore, 0, 21));
  if (hasInvalidRecordedScore) {
    return {
      ...invalidSuggestion(
        automaticInterpretation,
        'Há pontuação registrada fora da faixa possível de 0 a 21 pontos no HADS.',
        secondaryMeasures,
      ),
      validity: {
        status: 'invalid',
        note: 'Há pontuação registrada fora da faixa possível de 0 a 21 pontos no HADS.',
        limitations: [
          'O HADS é um instrumento de rastreio e não substitui diagnóstico clínico.',
        ],
      },
    };
  }

  if (validatedScoring && answersComplete) {
    const computed = calculateHADSScores(answers as number[]);
    if (
      computed.anxietyScore !== anxietyScore
      || computed.depressionScore !== depressionScore
    ) {
      return invalidSuggestion(
        automaticInterpretation,
        'As pontuações HADS não correspondem à chave validada aplicada às 14 respostas registradas.',
        secondaryMeasures,
      );
    }

    const worstScore = Math.max(computed.anxietyScore, computed.depressionScore);
    return {
      automaticInterpretation: getHADSInterpretationText(
        computed.anxietyScore,
        computed.depressionScore,
      ),
      status: worstScore >= 9 ? 'altered' : 'within_expected',
      basis: 'instrument_reference',
      referenceLabel: 'Botega et al. (1995) — HADS-A/HADS-D; rastreio positivo ≥ 9 pontos',
      validity: {
        status: 'valid',
        note: null,
        limitations: [
          'O HADS é um instrumento de rastreio e não substitui diagnóstico clínico.',
        ],
      },
      secondaryMeasures,
    };
  }

  return {
    automaticInterpretation,
    status: 'not_classified',
    basis: 'descriptive_only',
    referenceLabel: 'Zigmond e Snaith (1983); Botega et al. (1995) — registro legado sem chave de pontuação confirmada',
    validity: {
      status: 'not_assessed',
      note:
        'A correspondência entre as respostas registradas e a pontuação dos itens não pôde ser confirmada com segurança; por isso, o resultado é apresentado sem classificação automática.',
      limitations: [
        'O HADS é um instrumento de rastreio e não substitui diagnóstico clínico.',
      ],
    },
    secondaryMeasures,
  };
};

const buildMmrcSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const payload = asRecord(result.payloadJson);
  const summary = asRecord(result.summaryJson);
  const grade = readNumber(payload.grade, summary.grade);
  const automaticInterpretation = getMMRCInterpretationText(grade);

  if (!isFiniteIntegerInRange(grade, 0, 4)) {
    return invalidSuggestion(
      automaticInterpretation,
      'O grau mMRC deve estar entre 0 e 4.',
    );
  }

  return {
    automaticInterpretation,
    status: 'not_classified',
    basis: 'descriptive_only',
    referenceLabel: 'Bestall et al. (1999); Kovelis et al. (2008) — escala mMRC de 0 a 4',
    validity: {
      status: 'valid',
      note: 'O grau é apresentado sem classificação prognóstica automática; sua interpretação depende da população e do contexto clínico.',
      limitations: [],
    },
  };
};

const buildRespiratoryPressureSuggestion = (
  result: AssessmentResultV2,
  kind: 'PIMAX' | 'PEMAX',
): AdapterSuggestion => {
  const payload = hydrateRespiratoryPressurePayload(result);
  const summary = kind === 'PIMAX'
    ? summarizePimaxPayload(payload as PimaxResultPayload)
    : summarizePemaxPayload(payload as PemaxResultPayload);
  const automaticInterpretation = kind === 'PIMAX'
    ? getPimaxInterpretationText(payload as PimaxResultPayload)
    : getPemaxInterpretationText(payload as PemaxResultPayload);
  const label = kind === 'PIMAX' ? 'PImax' : 'PEmax';
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];

  if (summary.predicted != null) {
    secondaryMeasures.push(measure('predicted', 'Valor previsto', summary.predicted, `${formatNumber(summary.predicted, 1)} cmH2O`, 'cmH2O'));
  }
  if (summary.percentPredicted != null) {
    secondaryMeasures.push(measure('percent_predicted', 'Percentual do previsto', summary.percentPredicted, `${formatNumber(summary.percentPredicted, 1)}%`, '%'));
  }
  if (summary.lowerLimit != null) {
    secondaryMeasures.push(measure('derived_lln', 'Limite inferior esperado', summary.lowerLimit, `${formatNumber(summary.lowerLimit, 1)} cmH2O`, 'cmH2O'));
  }

  if (summary.max == null) {
    return invalidSuggestion(
      automaticInterpretation,
      `${label} sem medida registrada para interpretação.`,
      secondaryMeasures,
    );
  }

  const qualityLimited = !summary.quality.meetsCriteria;
  const qualityNote = qualityLimited
    ? `${label}: ${summary.quality.maneuversAnalyzed} ${summary.quality.maneuversAnalyzed === 1 ? 'manobra registrada' : 'manobras registradas'}; os critérios recomendados de quantidade ou reprodutibilidade não foram demonstrados.`
    : null;
  const validity: AssessmentReportValiditySnapshotV2 = {
    status: qualityLimited ? 'limited' : 'valid',
    note: qualityNote,
    limitations: qualityNote ? [qualityNote] : [],
  };

  if (summary.lowerLimit == null || summary.belowLowerLimit == null) {
    return {
      automaticInterpretation,
      status: 'not_classified',
      basis: 'descriptive_only',
      referenceLabel: summary.referenceApplicable
        ? 'Neder et al. (1999) — valor previsto; limite inferior individual indisponível'
        : null,
      validity,
      secondaryMeasures,
    };
  }

  return {
    automaticInterpretation,
    status: summary.belowLowerLimit ? 'altered' : 'within_expected',
    basis: 'instrument_reference',
    referenceLabel: `Neder et al. (1999) — limite inferior esperado: ${formatNumber(summary.lowerLimit, 1)} cmH2O`,
    validity,
    secondaryMeasures,
  };
};

const buildPeakFlowSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const summary = summarizePeakFlowPayload(result.payloadJson);
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];
  const sentences: string[] = [];
  const hasReference = summary.referenceMode !== 'none' && summary.referenceValue != null;

  if (summary.bestPreFlow != null) {
    secondaryMeasures.push(measure('best_pre_flow', 'Melhor pré-broncodilatador', summary.bestPreFlow, `${formatNumber(summary.bestPreFlow, 0)} L/min`, 'L/min'));
    const referenceSegment = hasReference && summary.referencePercentPre != null
      ? ` (${formatNumber(summary.referencePercentPre, 1)}% do ${summary.referenceLabel})`
      : '';
    const zoneSegment = summary.zone !== 'unclassified' ? `; ${summary.zoneLabel.toLocaleLowerCase('pt-BR')}` : '';
    sentences.push(`Pico de fluxo expiratório pré-broncodilatador de ${formatNumber(summary.bestPreFlow, 0)} L/min${referenceSegment}${zoneSegment}.`);
  }
  if (summary.bestPostFlow != null) {
    secondaryMeasures.push(measure('best_post_flow', 'Melhor pós-broncodilatador', summary.bestPostFlow, `${formatNumber(summary.bestPostFlow, 0)} L/min`, 'L/min'));
    sentences.push(`Após broncodilatador: ${formatNumber(summary.bestPostFlow, 0)} L/min.`);
  }
  if (hasReference && summary.referenceValue != null) {
    secondaryMeasures.push(measure('reference_value', summary.referenceLabel, summary.referenceValue, `${formatNumber(summary.referenceValue, 0)} L/min`, 'L/min'));
    if (summary.referencePercentPre != null) {
      secondaryMeasures.push(measure('reference_percent_pre', 'Percentual da referência pré', summary.referencePercentPre, `${formatNumber(summary.referencePercentPre, 1)}%`, '%'));
    }
    if (summary.referencePercentPost != null) {
      secondaryMeasures.push(measure('reference_percent_post', 'Percentual da referência pós', summary.referencePercentPost, `${formatNumber(summary.referencePercentPost, 1)}%`, '%'));
    }
  }
  if (summary.bronchodilatorDeltaAbsolute != null && summary.bronchodilatorDeltaPercent != null) {
    secondaryMeasures.push(
      measure('bronchodilator_delta_absolute', 'Variação após broncodilatador', summary.bronchodilatorDeltaAbsolute, `${formatNumber(summary.bronchodilatorDeltaAbsolute, 0)} L/min`, 'L/min'),
      measure('bronchodilator_delta_percent', 'Variação percentual após broncodilatador', summary.bronchodilatorDeltaPercent, `${formatNumber(summary.bronchodilatorDeltaPercent, 1)}%`, '%'),
    );
    sentences.push(`Variação após broncodilatador: ${formatNumber(summary.bronchodilatorDeltaAbsolute, 0)} L/min (${formatNumber(summary.bronchodilatorDeltaPercent, 1)}%).`);
  }
  if (summary.symptomLabels.length) sentences.push(`Sintomas registrados: ${summary.symptomLabels.join(', ')}.`);
  if (summary.dangerSignLabels.length) sentences.push(`Sinais de alarme registrados: ${summary.dangerSignLabels.join(', ')}.`);

  if (summary.bestPreFlow == null && summary.bestPostFlow == null) {
    return invalidSuggestion('Sem medida de pico de fluxo expiratório registrada.', 'Não há medida positiva de pico de fluxo expiratório registrada.', secondaryMeasures);
  }

  const limitations: string[] = [];
  if (summary.bestPreFlow == null) {
    limitations.push('Apenas a etapa pós-broncodilatador possui medidas registradas.');
  } else if (summary.preAttemptCount < 3) {
    limitations.push(`Etapa pré-broncodilatador com ${summary.preAttemptCount} ${summary.preAttemptCount === 1 ? 'manobra registrada' : 'manobras registradas'}.`);
  }
  if (summary.bronchodilatorUsed && summary.postAttemptCount < 3) {
    limitations.push(`Etapa pós-broncodilatador com ${summary.postAttemptCount} ${summary.postAttemptCount === 1 ? 'manobra registrada' : 'manobras registradas'}.`);
  }

  // As zonas já calculadas na origem usam o melhor pessoal. Uma referência
  // manual ou prevista permite descrever o percentual, sem criar normalidade.
  const status: AssessmentReportFindingStatus = summary.zone === 'green'
    ? 'within_expected'
    : summary.zone === 'yellow'
      ? 'borderline'
      : summary.zone === 'red'
        ? 'altered'
        : 'not_classified';

  return {
    automaticInterpretation: sentences.join(' '),
    status,
    basis: summary.zone === 'unclassified' ? 'descriptive_only' : 'instrument_reference',
    referenceLabel: hasReference && summary.referenceValue != null
      ? [
          `${summary.referenceLabel}: ${formatNumber(summary.referenceValue, 0)} L/min`,
          summary.referenceSourceLabel,
        ].filter(Boolean).join(' — ')
      : null,
    validity: {
      status: limitations.length ? 'limited' : 'valid',
      note: limitations.length ? limitations.join(' ') : null,
      limitations,
    },
    secondaryMeasures,
  };
};

const buildSIndexSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const payload = hydrateSIndexPayload(result);
  const summary = summarizeSIndexPayload(payload);
  const automaticInterpretation = getSIndexInterpretationText(payload);
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];
  if (summary.predictedSIndex != null) {
    secondaryMeasures.push(measure('predicted', 'Valor previsto', summary.predictedSIndex, `${formatNumber(summary.predictedSIndex, 1)} cmH2O`, 'cmH2O'));
  }
  if (summary.llnSIndex != null) {
    secondaryMeasures.push(measure('lln', 'Limite inferior esperado', summary.llnSIndex, `${formatNumber(summary.llnSIndex, 1)} cmH2O`, 'cmH2O'));
  }

  if (summary.maxPressure == null) {
    return invalidSuggestion(automaticInterpretation, 'S-Index sem medida válida para interpretação.', secondaryMeasures);
  }

  if (summary.llnSIndex == null || summary.belowLLN == null) {
    return {
      automaticInterpretation,
      status: 'not_classified',
      basis: 'descriptive_only',
      referenceLabel: summary.referenceLabel,
      validity: {
        status: 'valid',
        note: 'O valor previsto é apresentado sem classificação clínica porque não há limite inferior elegível para esta combinação de equação e dados.',
        limitations: [],
      },
      secondaryMeasures,
    };
  }

  return {
    automaticInterpretation,
    status: summary.belowLLN ? 'altered' : 'within_expected',
    basis: 'instrument_reference',
    referenceLabel: `${summary.referenceLabel} — limite inferior esperado: ${formatNumber(summary.llnSIndex, 1)} cmH2O`,
    validity: { status: 'valid', note: null, limitations: [] },
    secondaryMeasures,
  };
};

const buildTc6Suggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const summary = summarizeTc6Result(result.payloadJson);
  const automaticInterpretation = resolveTC6Interpretation(result.payloadJson);
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];

  if (summary.predictedDistance_m != null) {
    secondaryMeasures.push(
      measure(
        'predicted_distance',
        'Distância prevista',
        summary.predictedDistance_m,
        `${formatNumber(summary.predictedDistance_m, 1)} m`,
        'm',
      ),
    );
  }
  if (summary.percentPredicted != null) {
    secondaryMeasures.push(
      measure(
        'percent_predicted',
        'Percentual do previsto',
        summary.percentPredicted,
        `${formatNumber(summary.percentPredicted, 1)}%`,
        '%',
      ),
    );
  }
  if (summary.minSpo2 != null) {
    secondaryMeasures.push(
      measure(
        'minimum_spo2',
        'Menor SpO2',
        summary.minSpo2,
        `${formatNumber(summary.minSpo2, 0)}%`,
        '%',
      ),
    );
  }
  if (summary.spo2Drop != null) {
    secondaryMeasures.push(
      measure(
        'spo2_drop',
        'Queda de SpO2',
        summary.spo2Drop,
        `${formatNumber(summary.spo2Drop, 1)} pontos percentuais`,
        'p.p.',
      ),
    );
  }

  if (
    summary.workflowStatus !== 'completed'
    || summary.bestAttempt == null
    || summary.bestDistance_m == null
  ) {
    return invalidSuggestion(
      automaticInterpretation,
      'O TC6 não possui workflow concluído e uma tentativa principal válida para sustentar a interpretação.',
      secondaryMeasures,
    );
  }

  if (summary.hasSignificantDesaturation) {
    return {
      automaticInterpretation,
      status: 'altered',
      basis: 'instrument_reference',
      referenceLabel:
        'TC6 — dessaturação significativa conforme critério registrado (queda de SpO2 ≥ 4 pontos percentuais ou nadir < 85%)',
      validity: {
        status: 'valid',
        note: null,
        limitations: [],
      },
      secondaryMeasures,
    };
  }

  return {
    automaticInterpretation,
    status: 'not_classified',
    basis: 'descriptive_only',
    referenceLabel: null,
    validity: {
      status: 'valid',
      note:
        summary.percentPredicted == null
          ? 'O teste foi concluído, mas não houve dados suficientes para comparar automaticamente a distância com um valor previsto.'
          : `A distância e o percentual previsto pela equação ${summary.selectedReferenceLabel} são apresentados sem classificação automática; não há ponto de corte rastreável configurado para esta situação.`,
      limitations: [],
    },
    secondaryMeasures,
  };
};

const buildTugSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const summary = summarizeTugResult(result.payloadJson);
  const payload = asRecord(result.payloadJson);
  const automaticInterpretation = summary.interpretation;
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];

  if (summary.referenceComparison?.predictedTimeSeconds != null) {
    secondaryMeasures.push(
      measure(
        'predicted_time',
        'Tempo previsto',
        summary.referenceComparison.predictedTimeSeconds,
        `${formatNumber(summary.referenceComparison.predictedTimeSeconds, 1)} s`,
        's',
      ),
    );
  }
  if (summary.referenceComparison?.upperLimitSeconds != null) {
    secondaryMeasures.push(
      measure(
        'upper_normal_limit',
        'Limite superior de normalidade',
        summary.referenceComparison.upperLimitSeconds,
        `${formatNumber(summary.referenceComparison.upperLimitSeconds, 1)} s`,
        's',
      ),
    );
  }
  if (summary.referenceComparison?.percentPredicted != null) {
    secondaryMeasures.push(
      measure(
        'percent_predicted',
        'Percentual do previsto',
        summary.referenceComparison.percentPredicted,
        `${formatNumber(summary.referenceComparison.percentPredicted, 0)}%`,
        '%',
      ),
    );
  }

  if (!summary.completed || summary.bestTimeSeconds == null) {
    const reason = readString(summary.bestAttemptPayload?.reasonNotCompleted);
    return invalidSuggestion(
      automaticInterpretation,
      reason
        ? `O TUG não foi concluído. Motivo registrado: ${reason}.`
        : 'O TUG não foi concluído e não possui tempo válido para sustentar a interpretação.',
      secondaryMeasures,
    );
  }

  if (summary.physicalAssistance || !summary.familiarizationPerformed) {
    const limitation = summary.physicalAssistance
      ? 'Houve necessidade de assistência física; o tempo não é diretamente comparável aos pontos de corte do TUG.'
      : 'Não há registro de familiarização; a comparabilidade com o protocolo e seus pontos de corte está reduzida.';
    return {
      automaticInterpretation,
      status: 'not_interpretable',
      basis: 'descriptive_only',
      referenceLabel: null,
      validity: {
        status: 'limited',
        note: limitation,
        limitations: [limitation],
      },
      secondaryMeasures,
    };
  }

  const hasActiveFlag = summary.clinicalFlags.some((flag) => flag.active);
  const status: AssessmentReportFindingStatus =
    hasActiveFlag ? 'altered' : 'within_expected';
  const ageOutsideReference =
    summary.referenceComparison?.ageWithinReferenceRange === false;
  const extrapolationNote = ageOutsideReference
    ? getTUGReferenceComparisonNote(
        summary.referenceComparison,
        asRecord(payload.patientContext),
      )
    : null;

  return {
    automaticInterpretation,
    status,
    basis: 'instrument_reference',
    referenceLabel: `TUG — ${summary.severityRange}`,
    validity: {
      status: ageOutsideReference ? 'limited' : 'valid',
      note:
        extrapolationNote
        || (
          summary.referenceComparison
            ? null
            : 'Não foi possível calcular a referência individual; a leitura considera apenas os pontos de corte gerais registrados no instrumento.'
        ),
      limitations: extrapolationNote ? [extrapolationNote] : [],
    },
    secondaryMeasures,
  };
};

const buildGaitSpeedSuggestion = (
  result: AssessmentResultV2,
): AdapterSuggestion => {
  const summary = summarizeGaitSpeedResult(result.payloadJson);
  const automaticInterpretation = summary.interpretation;
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];

  if (summary.bestTimeSeconds != null) {
    secondaryMeasures.push(
      measure(
        'best_time',
        'Melhor tempo',
        summary.bestTimeSeconds,
        `${formatNumber(summary.bestTimeSeconds, 2)} s`,
        's',
      ),
    );
  }
  if (summary.referenceComparison?.predictedSpeedMs != null) {
    secondaryMeasures.push(
      measure(
        'predicted_speed',
        'Velocidade prevista',
        summary.referenceComparison.predictedSpeedMs,
        `${formatNumber(summary.referenceComparison.predictedSpeedMs, 2)} m/s`,
        'm/s',
      ),
    );
  }
  if (summary.referenceComparison?.lowerLimitSpeedMs != null) {
    secondaryMeasures.push(
      measure(
        'lower_normal_limit',
        'Limite inferior de normalidade',
        summary.referenceComparison.lowerLimitSpeedMs,
        `${formatNumber(summary.referenceComparison.lowerLimitSpeedMs, 2)} m/s`,
        'm/s',
      ),
    );
  }
  if (summary.referenceComparison?.percentPredicted != null) {
    secondaryMeasures.push(
      measure(
        'percent_predicted',
        'Percentual do previsto',
        summary.referenceComparison.percentPredicted,
        `${formatNumber(summary.referenceComparison.percentPredicted, 0)}%`,
        '%',
      ),
    );
  }

  if (!summary.completed || summary.bestSpeedMs == null) {
    const reason = readString(summary.bestAttemptPayload?.reasonNotCompleted);
    return invalidSuggestion(
      automaticInterpretation,
      reason
        ? `O 4MGS não foi concluído. Motivo registrado: ${reason}.`
        : 'O 4MGS não foi concluído e não possui velocidade válida para sustentar a interpretação.',
      secondaryMeasures,
    );
  }

  if (summary.physicalAssistance || !summary.comparableToReference) {
    const limitation =
      'Houve necessidade de assistência física; a velocidade não é diretamente comparável aos pontos de corte do 4MGS.';
    return {
      automaticInterpretation,
      status: 'not_interpretable',
      basis: 'descriptive_only',
      referenceLabel: null,
      validity: {
        status: 'limited',
        note: limitation,
        limitations: [limitation],
      },
      secondaryMeasures,
    };
  }

  const hasActiveFlag = summary.clinicalFlags.some((flag) => flag.active);
  return {
    automaticInterpretation,
    status: hasActiveFlag ? 'altered' : 'within_expected',
    basis: 'instrument_reference',
    referenceLabel: `4MGS — ${summary.severityRange}`,
    validity: {
      status: 'valid',
      note:
        summary.referenceComparison
          ? null
          : 'Não foi possível calcular a referência individual; a leitura considera o ponto de corte geral de 0,8 m/s registrado no instrumento.',
      limitations: [],
    },
    secondaryMeasures,
  };
};

const buildStsSuggestion = (
  result: AssessmentResultV2,
  testKey: StsTestKey,
): AdapterSuggestion => {
  const summary = summarizeStsResult(testKey, result.payloadJson);
  const rawPayload = asRecord(result.payloadJson);
  const automaticInterpretation = summary.interpretation;
  const comparison = summary.referenceComparison;
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [
    measure(
      'valid_attempts',
      'Tentativas válidas',
      summary.attemptCount,
      String(summary.attemptCount),
      'tentativas',
    ),
  ];

  if (!summary.completed || summary.best == null) {
    return invalidSuggestion(
      automaticInterpretation,
      summary.workflowStatus === 'awaiting_second_attempt'
        ? 'O protocolo do teste de sentar-levantar ainda aguarda a tentativa necessária para conclusão.'
        : 'O teste de sentar-levantar não possui tentativa válida suficiente para interpretação.',
      secondaryMeasures,
    );
  }

  const completedAttempts = [summary.attempt1, summary.attempt2].filter(
    (attempt): attempt is NonNullable<typeof attempt> => Boolean(attempt),
  );
  const recordedChairHeights =
    testKey === '5xSTS'
      ? [summary.chairHeightCm]
      : completedAttempts.map((attempt) => attempt.chairHeightCm);
  const chairHeightMissing =
    recordedChairHeights.length === 0
    || recordedChairHeights.some((height) => height == null);
  const incompatibleChairHeights = [
    ...new Set(
      recordedChairHeights.filter(
        (height): height is number =>
          typeof height === 'number'
          && height !== STS_REFERENCE_CHAIR_HEIGHT_CM,
      ),
    ),
  ];
  const protocolLimitations = [
    summary.usedArms === true
      ? 'Houve uso dos braços no 5xSTS, condição que impede comparação direta com a referência normativa adotada.'
      : '',
    testKey === '5xSTS' && typeof rawPayload.usedArms !== 'boolean'
      ? 'O uso ou não dos braços não foi registrado no 5xSTS; a condição padronizada da referência normativa não pôde ser confirmada.'
      : '',
    chairHeightMissing
      ? `A altura da cadeira não foi registrada em todas as tentativas; a referência normativa adotada utiliza cadeira de ${STS_REFERENCE_CHAIR_HEIGHT_CM} cm.`
      : '',
    incompatibleChairHeights.length > 0
      ? `A altura registrada da cadeira (${incompatibleChairHeights
          .map((height) => `${formatNumber(height, 0)} cm`)
          .join(', ')}) difere dos ${STS_REFERENCE_CHAIR_HEIGHT_CM} cm utilizados pela referência normativa adotada.`
      : '',
  ].filter(Boolean);

  if (protocolLimitations.length > 0) {
    const observedResult = getStsReferenceUnit(testKey, summary.best);
    return {
      automaticInterpretation:
        `Resultado observado: ${observedResult}. A comparação normativa automática não foi aplicada devido às condições de execução registradas.`,
      status: summary.usedArms === true ? 'not_interpretable' : 'not_classified',
      basis: 'descriptive_only',
      referenceLabel: null,
      validity: {
        status: 'limited',
        note:
          'A comparação normativa automática não foi aplicada devido às condições de execução registradas.',
        limitations: protocolLimitations,
      },
      secondaryMeasures,
    };
  }

  if (!comparison) {
    return {
      automaticInterpretation,
      status: 'not_classified',
      basis: 'descriptive_only',
      referenceLabel: null,
      validity: {
        status: 'valid',
        note:
          'O teste foi concluído, mas não foi possível calcular uma referência individual segura; o resultado foi incluído sem classificação automática.',
        limitations: [],
      },
      secondaryMeasures,
    };
  }

  secondaryMeasures.push(
    measure(
      'predicted_value',
      'Valor previsto',
      comparison.predictedValue,
      getStsReferenceUnit(testKey, comparison.predictedValue),
      summary.measurementUnit,
    ),
    measure(
      'normal_limit',
      comparison.normalLimitDirection === 'upper'
        ? 'Limite superior de normalidade'
        : 'Limite inferior de normalidade',
      comparison.normalLimitValue,
      getStsReferenceUnit(testKey, comparison.normalLimitValue),
      summary.measurementUnit,
    ),
    measure(
      'percent_predicted',
      'Percentual do previsto',
      comparison.percentPredicted,
      `${formatNumber(comparison.percentPredicted, 0)}%`,
      '%',
    ),
  );

  const validationNote = getStsReferenceValidationNote(comparison);
  const limitLabel =
    comparison.normalLimitDirection === 'upper'
      ? 'limite superior de normalidade'
      : 'limite inferior de normalidade';

  return {
    automaticInterpretation,
    status: comparison.withinNormalLimit ? 'within_expected' : 'altered',
    basis: 'instrument_reference',
    referenceLabel:
      `Furlanetto et al. (2022) — ${limitLabel}: ${getStsReferenceUnit(testKey, comparison.normalLimitValue)}`,
    validity: {
      status: validationNote ? 'limited' : 'valid',
      note: validationNote || null,
      limitations: validationNote ? [validationNote] : [],
    },
    secondaryMeasures,
  };
};

const buildNeuromuscularSuggestion = (result: AssessmentResultV2): AdapterSuggestion => {
  const payload = asRecord(result.payloadJson);
  // The report resolves the professional override separately from its automatic suggestion.
  const automaticPayload = { ...payload, interpretation: null, report: null };
  if (result.testKey === 'PEAK_COUGH_FLOW') {
    const summary = summarizePeakCoughFlowPayload(automaticPayload);
    const normalized = normalizePeakCoughFlowPayload(automaticPayload);
    const secondaryMeasures = summary.series.filter((series) => series.bestFlow != null).map((series) =>
      measure(series.condition, series.label, series.bestFlow, `${formatNumber(series.bestFlow!, 1)} L/min`, 'L/min'));
    const reference = summary.referenceAssessment;
    if (reference.available) {
      const values = reference.referenceId === 'BIANCHI_2008'
        ? [['cough_p5', '5º percentil da referência', reference.p5], ['cough_p50', 'Mediana da referência', reference.p50], ['cough_p95', '95º percentil da referência', reference.p95]] as const
        : [['cough_mean_three', 'Média das três manobras', reference.comparisonValue], ['cough_reference_mean', 'Média publicada', reference.mean], ['cough_reference_sd', 'Desvio padrão do grupo', reference.sd]] as const;
      for (const [key, label, value] of values) {
        if (value != null) secondaryMeasures.push(measure(key, label, value, `${formatNumber(value, 1)} L/min`, 'L/min'));
      }
    }
    if (summary.bestFlow == null) return invalidSuggestion(summary.interpretation, 'Não há manobra aceita de pico de fluxo de tosse.', secondaryMeasures);
    const spontaneous = summary.series.find((series) => series.condition === 'unassisted');
    const limitations = [...summary.warningMessages];
    if (!normalized?.position || !normalized.device) limitations.push('Posição ou equipamento não registrados; considerar essa limitação ao interpretar a medida.');
    const technicallyLimited = limitations.length > 0 || (spontaneous?.repeatable == null && summary.bestUnassistedFlow != null);
    const lowFlow = summary.classifiable && summary.bestUnassistedFlow! < 270 && !technicallyLimited;
    const pediatricReference = reference.available && reference.referenceId === 'BIANCHI_2008';
    const belowP5 = pediatricReference && reference.p5 != null && reference.comparisonValue != null && reference.comparisonValue < reference.p5;
    const normativeClassification = pediatricReference && !technicallyLimited;
    return {
      automaticInterpretation: summary.interpretation,
      status: lowFlow || (normativeClassification && belowP5) ? 'altered' : normativeClassification ? 'within_expected' : 'not_classified',
      basis: lowFlow || normativeClassification ? 'instrument_reference' : 'descriptive_only',
      referenceLabel: [summary.classifiable ? 'ERS 2019; CHEST 2023; DMD UK 2024 — limiares clínicos de baixo fluxo' : null, reference.available ? reference.sourceLabel : null].filter(Boolean).join('; ') || null,
      validity: {
        status: technicallyLimited ? 'limited' : 'valid',
        note: technicallyLimited ? 'A interpretação exige considerar as limitações de qualidade registradas.' : summary.classifiable
          ? 'A ausência de alerta por baixo fluxo não equivale a normalidade nem garante depuração adequada de secreções.'
          : reference.interpretation,
        limitations,
      },
      secondaryMeasures,
      allowLegacyDirectInterpretation: false,
    };
  }
  if (result.testKey === 'CV_POSTURAL') {
    const summary = summarizePosturalVitalCapacityPayload(automaticPayload);
    const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];
    if (summary.bestSeated != null) secondaryMeasures.push(measure('seated_capacity', 'Capacidade vital sentada', summary.bestSeated, `${formatNumber(summary.bestSeated, 2)} L`, 'L'));
    if (summary.bestSupine != null) secondaryMeasures.push(measure('supine_capacity', 'Capacidade vital supina', summary.bestSupine, `${formatNumber(summary.bestSupine, 2)} L`, 'L'));
    if (summary.reductionPercent != null) secondaryMeasures.push(measure('postural_reduction', 'Redução postural', summary.reductionPercent, `${formatNumber(summary.reductionPercent, 1)}%`, '%'));
    if (!secondaryMeasures.length) return invalidSuggestion(summary.interpretation, 'Não há medida aceitável de capacidade vital.', secondaryMeasures);
    const limited = summary.qualityLimited || summary.reductionPercent == null;
    return {
      automaticInterpretation: summary.interpretation,
      status: 'not_classified', basis: 'descriptive_only', referenceLabel: 'Fromageot 2001; ERS 2019 — comparação postural descritiva',
      validity: { status: limited ? 'limited' : 'valid', note: summary.reductionPercent == null ? 'Falta uma das posições para calcular a redução postural.' : 'A variação postural isolada não diagnostica nem exclui comprometimento diafragmático.', limitations: summary.caveats },
      secondaryMeasures, allowLegacyDirectInterpretation: false,
    };
  }
  const summary = summarizeSnipPayload(automaticPayload);
  const secondaryMeasures: AssessmentReportMeasureSnapshotV2[] = [];
  if (summary.bestLeft != null) secondaryMeasures.push(measure('snip_left', 'SNIP — narina esquerda', summary.bestLeft, `${formatNumber(summary.bestLeft, 1)} cmH2O`, 'cmH2O'));
  if (summary.bestRight != null) secondaryMeasures.push(measure('snip_right', 'SNIP — narina direita', summary.bestRight, `${formatNumber(summary.bestRight, 1)} cmH2O`, 'cmH2O'));
  if (summary.max == null) return invalidSuggestion(summary.interpretation, 'Não há manobra aceitável de SNIP.', secondaryMeasures);
  return {
    automaticInterpretation: summary.interpretation,
    status: 'not_classified', basis: 'descriptive_only', referenceLabel: 'ERS 2019; ARTP 2020 — sem classificação normativa automática',
    validity: { status: summary.qualityLimited ? 'limited' : 'valid', note: 'O SNIP complementa a PImax; não foi aplicada uma faixa normativa automática.', limitations: summary.caveats },
    secondaryMeasures, allowLegacyDirectInterpretation: false,
  };
};

const getSimpleAutomaticInterpretation = (
  result: AssessmentResultV2,
): string => {
  const payload = asRecord(result.payloadJson);
  const summary = asRecord(result.summaryJson);

  switch (result.testKey) {
    case 'ACT':
      return getACTInterpretationText(readNumber(payload.total, summary.total));
    case 'mMRC':
      return getMMRCInterpretationText(readNumber(payload.grade, summary.grade));
    case 'PSQI':
      return getPSQIInterpretationText(
        payload as unknown as Parameters<typeof getPSQIInterpretationText>[0],
      );
    case 'AQLQ':
      return getAQLQInterpretationText(
        readNumber(payload.total, summary.total),
        payload.domainScores as Parameters<typeof getAQLQInterpretationText>[1],
      );
    case 'SGRQ':
      return getSGRQInterpretationText(
        readNumber(payload.total, summary.total),
        {
          symptoms: readNumber(payload.symptoms, summary.symptoms),
          activity: readNumber(payload.activity, summary.activity),
          impact: readNumber(payload.impact, summary.impact),
        },
      );
    case 'LCADL':
      return getLCADLInterpretationText(
        readNumber(payload.percentage, summary.percentage),
      );
    case 'MLHFQ':
      return getMLHFQInterpretationText(
        readNumber(payload.total, summary.total),
        readNumber(asRecord(payload.physical).score, summary.physicalScore),
        readNumber(asRecord(payload.emotional).score, summary.emotionalScore),
      );
    case 'PFSDQ-M':
      return getPFSDQInterpretationText(
        readNumber(asRecord(payload.dyspnea).score, summary.dyspneaScore),
        readNumber(asRecord(payload.fatigue).score, summary.fatigueScore),
        readNumber(asRecord(payload.change).score, summary.changeScore),
      );
    case 'SAQ':
      return getSAQInterpretationText(
        payload.domainScores as Parameters<typeof getSAQInterpretationText>[0],
      );
    case 'ISWT':
      return resolveISWTInterpretation(result.payloadJson);
    case 'ESWT':
      return resolveESWTInterpretation(result.payloadJson);
    case '6MST':
      return resolveSixMSTInterpretation(result.payloadJson);
    case 'CHESTER':
      return resolveChesterInterpretation(result.payloadJson);
    case 'HANDGRIP':
      return getHandgripInterpretationText(
        payload as Parameters<typeof getHandgripInterpretationText>[0],
      );
    case 'PERIPHERAL_MUSCLE_STRENGTH':
      return getPeripheralStrengthInterpretationText(
        payload as Parameters<typeof getPeripheralStrengthInterpretationText>[0],
      );
    case 'ONE_RM':
      return getOneRMInterpretationText(
        payload as unknown as Parameters<typeof getOneRMInterpretationText>[0],
      );
    case 'PIMAX':
      return getPimaxInterpretationText(
        payload as unknown as Parameters<typeof getPimaxInterpretationText>[0],
      );
    case 'PEMAX':
      return getPemaxInterpretationText(
        payload as unknown as Parameters<typeof getPemaxInterpretationText>[0],
      );
    case 'S_INDEX':
      return getSIndexInterpretationText(
        payload as unknown as Parameters<typeof getSIndexInterpretationText>[0],
      );
    case 'RESP_ENDURANCE':
      return getRespiratoryEnduranceInterpretationText(
        payload as unknown as Parameters<typeof getRespiratoryEnduranceInterpretationText>[0],
      );
    case 'PEAK_FLOW':
      return summarizePeakFlowPayload(result.payloadJson).interpretation;
    case 'PEAK_COUGH_FLOW':
      return summarizePeakCoughFlowPayload(result.payloadJson).interpretation;
    case 'CV_POSTURAL':
      return summarizePosturalVitalCapacityPayload(result.payloadJson).interpretation;
    case 'SNIP':
      return summarizeSnipPayload(result.payloadJson).interpretation;
    default:
      return readString(summary.interpretation, payload.interpretation);
  }
};

const getAdapterSuggestion = (
  result: AssessmentResultV2,
): AdapterSuggestion => {
  const withoutLegacyDirectInterpretation = (
    suggestion: AdapterSuggestion,
  ): AdapterSuggestion => ({
    ...suggestion,
    allowLegacyDirectInterpretation: false,
  });

  switch (result.testKey) {
    case 'CAT':
      return buildCatSuggestion(result);
    case 'HADS':
      return withoutLegacyDirectInterpretation(buildHadsSuggestion(result));
    case 'mMRC':
      return withoutLegacyDirectInterpretation(buildMmrcSuggestion(result));
    case 'PIMAX':
    case 'PEMAX':
      return withoutLegacyDirectInterpretation(buildRespiratoryPressureSuggestion(result, result.testKey));
    case 'PEAK_FLOW':
      return withoutLegacyDirectInterpretation(buildPeakFlowSuggestion(result));
    case 'S_INDEX':
      return withoutLegacyDirectInterpretation(buildSIndexSuggestion(result));
    case 'TC6':
      return withoutLegacyDirectInterpretation(buildTc6Suggestion(result));
    case 'TUG':
      return buildTugSuggestion(result);
    case '4MGS':
      return buildGaitSpeedSuggestion(result);
    case '5xSTS':
    case 'STS_30S':
    case 'STS_1MIN':
      return withoutLegacyDirectInterpretation(
        buildStsSuggestion(result, result.testKey),
      );
    case 'PEAK_COUGH_FLOW':
    case 'CV_POSTURAL':
    case 'SNIP':
      return buildNeuromuscularSuggestion(result);
    default:
      return defaultSuggestion(getSimpleAutomaticInterpretation(result));
  }
};

const buildFallbackSummary = (
  testLabel: string,
  primaryMetric: string,
): string => {
  const hasMetric =
    primaryMetric
    && primaryMetric !== '—'
    && primaryMetric.toLocaleLowerCase('pt-BR') !== 'não disponível';

  if (!hasMetric) return '';
  return `${testLabel}: ${primaryMetric}${/[.!?…]$/.test(primaryMetric) ? '' : '.'}`;
};

export const buildAssessmentReportSourceSuggestion = ({
  result,
  domain,
  testLabel,
  primaryMetric,
  originalClassification,
  capturedAt,
}: BuildAssessmentReportSourceSuggestionInput): AssessmentReportSourceSnapshotV2 => {
  const safeTestLabel = testLabel.trim() || result.testKey;
  const safePrimaryMetric = primaryMetric.trim() || '—';
  const safeOriginalClassification = readString(originalClassification);
  const adapter = getAdapterSuggestion(result);
  const referenceDecisionPayload = result.testKey === 'PIMAX' || result.testKey === 'PEMAX'
    ? hydrateRespiratoryPressurePayload(result)
    : result.testKey === 'S_INDEX'
      ? hydrateSIndexPayload(result)
      : result.payloadJson;
  const persistedDecision = normalizeClinicalReferenceDecision(
    asRecord(result.payloadJson).clinicalReference,
  );
  const clinicalReference = persistedDecision ?? buildClinicalReferenceDecision({
    testKey: result.testKey,
    testVersion: result.testVersion,
    payload: referenceDecisionPayload,
    evaluatedAt: capturedAt,
  });
  const professionalOverride = getAssessmentInterpretationOverride(
    result.payloadJson,
  );
  const automaticInterpretation =
    adapter.allowLegacyDirectInterpretation === false
      ? adapter.automaticInterpretation.trim()
      : resolveAssessmentInterpretationText(
          result.payloadJson,
          adapter.automaticInterpretation,
        );
  const conciseSummary =
    professionalOverride
    || automaticInterpretation
    || buildFallbackSummary(
      safeTestLabel,
      safePrimaryMetric,
    );
  const lifecycleInvalid = result.status !== 'completed';
  const validity = lifecycleInvalid
    ? {
        status: 'invalid' as const,
        note:
          'A avaliação de origem não está concluída e não pode sustentar interpretação no relatório.',
        limitations: [],
      }
    : adapter.validity;
  const findingStatus = lifecycleInvalid
    ? 'not_interpretable' as const
    : adapter.status;
  const findingBasis = lifecycleInvalid
    ? 'descriptive_only' as const
    : adapter.basis;

  return {
    resultId: result.resultId,
    sessionId: result.sessionId,
    finding: {
      domain,
      status: findingStatus,
      basis: findingBasis,
      label: safeTestLabel,
      conciseSummary,
      referenceLabel: lifecycleInvalid
        ? null
        : adapter.referenceLabel ?? getClinicalReferencePrimaryLabel(clinicalReference),
    },
    validity,
    professional: {
      userId: result.professionalUserId ?? null,
      name: result.professionalNameSnapshot ?? null,
      registration: result.professionalRegistrationSnapshot ?? null,
      role: result.professionalRoleSnapshot ?? null,
    },
    snapshot: {
      testKey: result.testKey,
      testVersion: result.testVersion,
      testLabel: safeTestLabel,
      assessmentDate: result.assessmentDate,
      sourceStatus: result.status,
      sourceCreatedAt: result.createdAt ?? null,
      sourceUpdatedAt: result.updatedAt ?? result.createdAt ?? null,
      capturedAt,
      primaryMeasure: {
        key: 'primary_result',
        label: 'Resultado principal',
        rawValue: safePrimaryMetric,
        formattedValue: safePrimaryMetric,
        unit: null,
      },
      secondaryMeasures: adapter.secondaryMeasures ?? [],
      interpretationText: conciseSummary,
      referenceText: safeOriginalClassification || null,
      notes: result.notes?.trim() || null,
      longitudinalMeasures: extractAssessmentLongitudinalMeasures(result),
      ...(result.testKey === 'INITIAL_ASSESSMENT'
        ? { clinicalAssessment: captureClinicalAssessmentSnapshot(result.payloadJson) }
        : {}),
    },
    clinicalReference,
  };
};
